import { SpaceValidationError, SpaceValidationErrorCode } from 'types/space';

/**
 * Stable finding taxonomy shared by every conformance level (L0–L7) and every caller
 * (write routes, composer check, composer agent, jest corpus, publish gate, fleet sweep).
 * `code` is the cross-surface contract — the corpus, the composer UI and the sweep report
 * all key on it, so it is never derived from `message` (a free-text string a translator or
 * a later edit could change without anyone noticing the code moved with it).
 */

export type ConformanceLevel = 'L0' | 'L1' | 'L2' | 'L3' | 'L5' | 'L7';

export type ConformanceSeverity = 'error' | 'warning';

/** New codes introduced by the conformance checker. L0/L1 findings reuse `SpaceValidationErrorCode` verbatim (see `ConformanceFindingCode`) rather than duplicating them here. */
export type NewConformanceFindingCode =
  | 'REF_UNRESOLVED'
  | 'REF_VERSION_DRIFT'
  | 'PROP_REQUIRED_MISSING'
  | 'PROP_TYPE_MISMATCH'
  | 'PROP_ENUM_VIOLATION'
  | 'PROP_UNDECLARED'
  | 'SLOT_UNFILLED'
  | 'SLOT_UNDECLARED'
  | 'EMIT_UNDECLARED_TARGET'
  | 'INTERFACE_PROP_UNBOUND'
  | 'INTERFACE_EMIT_UNUSED'
  | 'PRIMITIVE_PROP_SCHEMA_VIOLATION'
  | 'KIND_CONTAINMENT_VIOLATION'
  | 'DATA_PATH_UNRESOLVED'
  | 'DATA_PATH_TYPE_MISMATCH'
  | 'DATA_PATH_ACCESS_GATED'
  | 'RENDER_ADAPTER_MISSING'
  | 'RENDER_THREW'
  | 'A11Y_VIOLATION'
  | 'DIFFERENTIAL_GEOMETRY_MISMATCH'
  | 'DIFFERENTIAL_TOOLBAR_MISMATCH';

/** The full finding code vocabulary: `verifyArtifact`'s L0/L1 levels reuse `SpaceValidationErrorCode` verbatim (structural/referential errors already have a stable taxonomy in `validateSpaceDocument`/`expandComposition`); L2+ introduce `NewConformanceFindingCode`. */
export type ConformanceFindingCode = SpaceValidationErrorCode | NewConformanceFindingCode;

/**
 * The `NewConformanceFindingCode` subset actually producible today, at the levels `verifyArtifact`
 * currently implements (L2 interface/containment, L3 data-contract). The remaining codes
 * (`REF_VERSION_DRIFT`, and the L5/L7 render/differential codes) belong to write-path lockfile
 * comparison (Phase 2) and render/differential levels (Phase 4) — neither exists yet, so a
 * coverage gate over the full `NewConformanceFindingCode` union would always report them missing.
 * This is the corpus coverage gate's required-code list (`corpus/coverage.ts`).
 */
export const IMPLEMENTED_L2_L3_FINDING_CODES: readonly NewConformanceFindingCode[] = [
  'REF_UNRESOLVED',
  'PROP_REQUIRED_MISSING',
  'PROP_TYPE_MISMATCH',
  'PROP_ENUM_VIOLATION',
  'PROP_UNDECLARED',
  'SLOT_UNFILLED',
  'SLOT_UNDECLARED',
  'EMIT_UNDECLARED_TARGET',
  'INTERFACE_PROP_UNBOUND',
  'INTERFACE_EMIT_UNUSED',
  'PRIMITIVE_PROP_SCHEMA_VIOLATION',
  'KIND_CONTAINMENT_VIOLATION',
  'DATA_PATH_UNRESOLVED',
  'DATA_PATH_TYPE_MISMATCH',
  'DATA_PATH_ACCESS_GATED',
];

/** Findings that are always a warning, never an error, regardless of call site. */
export const WARNING_ONLY_FINDING_CODES: ReadonlySet<ConformanceFindingCode> = new Set([
  'PROP_UNDECLARED',
  'INTERFACE_EMIT_UNUSED',
  'DATA_PATH_ACCESS_GATED',
]);

export interface ConformanceFinding {
  code: ConformanceFindingCode;
  level: ConformanceLevel;
  severity: ConformanceSeverity;
  message: string;
  /** JSON-Pointer-ish path into the document, e.g. `a2ui/components/<id>/<prop>`. */
  path?: string;
  ref?: { systemName: string; version?: number };
}

/** Constructs a finding, enforcing `WARNING_ONLY_FINDING_CODES` rather than trusting every call site to remember it. */
export function makeFinding(
  code: ConformanceFindingCode,
  level: ConformanceLevel,
  severity: ConformanceSeverity,
  message: string,
  extra: Pick<ConformanceFinding, 'path' | 'ref'> = {}
): ConformanceFinding {
  return {
    code,
    level,
    severity: WARNING_ONLY_FINDING_CODES.has(code) ? 'warning' : severity,
    message,
    ...extra,
  };
}

/** Wraps a `validateSpaceDocument`/`validateCompositionDefinition`/`expandComposition` error as an L0 or L1 finding — these validators are fail-closed, so every error they emit is `severity: 'error'`. */
export function spaceErrorToFinding(error: SpaceValidationError, level: 'L0' | 'L1'): ConformanceFinding {
  return { code: error.code, level, severity: 'error', message: error.message, path: error.path };
}
